import { ID_PART_PATTERN } from "../id-grammar.js";
import { LOWER_UUID_PATTERN } from "../kernel/kernel-internal.js";
import "./library-rights.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/library/library-instance-slug.ts
/**
* The slug of a library instance (2026-10-12-shared-libraries, R-002).
*
* When a consuming app runs on a data plane, the CP starts one instance of each library it uses.
* The CP allocates the instance as a `release.apps` row with `kind = 'instance'` and the slug that
* {@link libraryInstanceSlug} derives. Every per-app mechanism on the data plane keys on that slug
* (DEF-9), and core-api computes it again from the signed `lev` claim of a rights certificate
* (R-026, R-030). So the CP allocator, the certificate check and core-api must derive one value from
* one function.
*
* The contract `createAppSchema` in `app-admin-inputs.ts` refuses every slug that starts with
* {@link LIBRARY_INSTANCE_SLUG_PREFIX} (R-002, R-013). The real app create door, `POST /v1/apps` in
* release-engine, binds its own `createAppSchema` copy in `create-app.dto.ts`. That copy takes the
* refusal in stage 2 (WS-18), and the stage 2 migration refuses to run while a `release.apps` slug
* starts with `zl-`. Until both land, an app can take a `zl-` slug; the first instance allocation
* comes in stage 3 (WS-6), after both.
*
* Pure and browser-safe: SHA-256 comes from `@noble/hashes`.
*/
/**
* The reserved prefix of every instance slug (R-002). The contract `createAppSchema` refuses a slug
* that starts with it; the release-engine door takes the same refusal in stage 2 (WS-18).
*/
const LIBRARY_INSTANCE_SLUG_PREFIX = "zl-";
/** The longest instance slug: the `appId` cap of `app-manifest.schema.json` (R-002). */
const LIBRARY_INSTANCE_SLUG_MAX_LENGTH = 30;
/** The number of hex characters of the SHA-256 at the end of an instance slug. */
const LIBRARY_INSTANCE_SLUG_HASH_LENGTH = 8;
/**
* The longest library prefix inside an instance slug: 30 less `zl-`, the `-` before the hash and the
* 8 hex characters, so 18.
*/
const LIBRARY_INSTANCE_SLUG_PREFIX_MAX_LENGTH = 18;
/**
* The grammar of an instance slug: `zl-`, a library prefix of 1 to 18 characters that starts with a
* letter, has no `--` and no trailing `-`, then `-` and 8 lower-case hex characters. Every match
* also matches `ID_PART_PATTERN`, so `parseInstanceKey` reads it.
*/
const LIBRARY_INSTANCE_SLUG_PATTERN = /^zl-[a-z](?:[a-z0-9]|-(?=[a-z0-9])){0,17}-[0-9a-f]{8}$/;
/**
* True when `slug` is a string that starts with {@link LIBRARY_INSTANCE_SLUG_PREFIX}. App create
* refuses such a slug, whether or not it matches {@link LIBRARY_INSTANCE_SLUG_PATTERN}. Never
* throws. Pure.
*/
function isReservedInstanceSlug(slug) {
	return typeof slug === "string" && slug.startsWith("zl-");
}
/**
* True when `slug` matches {@link LIBRARY_INSTANCE_SLUG_PATTERN} within the 30-character cap. Never
* throws. Pure.
*/
function isLibraryInstanceSlug(slug) {
	return typeof slug === "string" && slug.length <= 30 && LIBRARY_INSTANCE_SLUG_PATTERN.test(slug);
}
/**
* The instance slug of the library `librarySlug` (CP app id `libraryId`) for the consuming app with
* CP app id `consumerId` (R-002):
*
* 1. The hash is the first 8 hex characters of the SHA-256 of the UTF-8 text
*    `<libraryId>:<consumerId>`.
* 2. The library prefix is `librarySlug` cut to 18 characters, with every trailing `-` trimmed
*    after the cut.
* 3. The slug is `zl-<prefix>-<hash>`, at most 30 characters.
*
* `libraryInstanceSlug('smoke-lib', L, C)` is `zl-smoke-lib-` and 8 hex characters. Equal inputs give
* equal slugs; the hash keeps two consumers of one library apart.
*
* It throws `TypeError` for a `librarySlug` that is longer than 30 characters or fails
* `ID_PART_PATTERN` (a leading digit, a `--` or a trailing `-`), and for a `libraryId` or
* `consumerId` that is not a lower-case UUID. Pure.
*/
function libraryInstanceSlug(librarySlug, libraryId, consumerId) {
	if (typeof librarySlug !== "string" || librarySlug.length > 30 || !ID_PART_PATTERN.test(librarySlug)) throw new TypeError(`libraryInstanceSlug: ${JSON.stringify(librarySlug)} is not a library slug`);
	if (typeof libraryId !== "string" || !LOWER_UUID_PATTERN.test(libraryId)) throw new TypeError(`libraryInstanceSlug: libraryId ${JSON.stringify(libraryId)} is not a lower-case UUID`);
	if (typeof consumerId !== "string" || !LOWER_UUID_PATTERN.test(consumerId)) throw new TypeError(`libraryInstanceSlug: consumerId ${JSON.stringify(consumerId)} is not a lower-case UUID`);
	const hash = bytesToHex(sha256(utf8ToBytes(`${libraryId}:${consumerId}`))).slice(0, 8);
	return `zl-${librarySlug.slice(0, 18).replace(/-+$/, "")}-${hash}`;
}
//#endregion
export { LIBRARY_INSTANCE_SLUG_HASH_LENGTH, LIBRARY_INSTANCE_SLUG_MAX_LENGTH, LIBRARY_INSTANCE_SLUG_PATTERN, LIBRARY_INSTANCE_SLUG_PREFIX, LIBRARY_INSTANCE_SLUG_PREFIX_MAX_LENGTH, isLibraryInstanceSlug, isReservedInstanceSlug, libraryInstanceSlug };

//# sourceMappingURL=library-instance-slug.js.map